import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Pencil, Loader2, Tag, X, Save, Copy, ToggleLeft, ToggleRight, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

var API = "/api";

function formatDate(dt) {
  if (!dt) return "—";
  var d = new Date(dt);
  return d.toLocaleDateString();
}

function isoLocalForInput(iso) {
  if (!iso) return "";
  var d = new Date(iso);
  var tzOff = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOff).toISOString().slice(0, 16);
}

function localToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

function emptyForm() {
  return {
    code: "",
    description: "",
    discount_type: "percentage",
    discount_value: "10",
    scope: "cart",
    product_ids: [],
    categories: [],
    starts_at: "",
    expires_at: "",
    max_uses: "",
    max_uses_per_customer: "",
    min_order_amount: "",
    free_shipping: false,
    enabled: true
  };
}

export default function CouponsTab() {
  var [coupons, setCoupons] = useState([]);
  var [products, setProducts] = useState([]);
  var [categories, setCategories] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showModal, setShowModal] = useState(false);
  var [editing, setEditing] = useState(null);
  var [form, setForm] = useState(emptyForm());
  var [saving, setSaving] = useState(false);

  function refresh() {
    setLoading(true);
    Promise.all([
      axios.get(API + "/coupons"),
      axios.get(API + "/products"),
      axios.get(API + "/categories")
    ])
      .then(function (results) {
        setCoupons(results[0].data || []);
        setProducts(results[1].data || []);
        setCategories((results[2].data || {}).product_categories || []);
      })
      .catch(function () { toast.error("Failed to load coupons"); })
      .finally(function () { setLoading(false); });
  }

  useEffect(function () { refresh(); }, []);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  }

  function openEdit(coupon) {
    setEditing(coupon);
    setForm({
      code: coupon.code || "",
      description: coupon.description || "",
      discount_type: coupon.discount_type || "percentage",
      discount_value: String(coupon.discount_value || ""),
      scope: coupon.scope || "cart",
      product_ids: coupon.product_ids || [],
      categories: coupon.categories || [],
      starts_at: isoLocalForInput(coupon.starts_at),
      expires_at: isoLocalForInput(coupon.expires_at),
      max_uses: coupon.max_uses != null ? String(coupon.max_uses) : "",
      max_uses_per_customer: coupon.max_uses_per_customer != null ? String(coupon.max_uses_per_customer) : "",
      min_order_amount: coupon.min_order_amount != null ? String(coupon.min_order_amount) : "",
      free_shipping: !!coupon.free_shipping,
      enabled: coupon.enabled !== false
    });
    setShowModal(true);
  }

  function setField(key, value) {
    setForm(function (f) {
      var n = {};
      Object.keys(f).forEach(function (k) { n[k] = f[k]; });
      n[key] = value;
      return n;
    });
  }

  function toggleArrayValue(key, value) {
    setForm(function (f) {
      var arr = (f[key] || []).slice();
      var idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(value);
      var n = {};
      Object.keys(f).forEach(function (k) { n[k] = f[k]; });
      n[key] = arr;
      return n;
    });
  }

  function save() {
    if (!form.code.trim()) { toast.error("Code is required"); return; }
    var dv = parseFloat(form.discount_value);
    if (isNaN(dv) || dv <= 0) { toast.error("Discount value must be greater than zero"); return; }
    if (form.discount_type === "percentage" && dv > 100) { toast.error("Percentage cannot exceed 100"); return; }
    if (form.scope === "products" && form.product_ids.length === 0) { toast.error("Pick at least one product"); return; }
    if (form.scope === "categories" && form.categories.length === 0) { toast.error("Pick at least one category"); return; }

    var payload = {
      code: form.code.trim(),
      description: form.description,
      discount_type: form.discount_type,
      discount_value: dv,
      scope: form.scope,
      product_ids: form.scope === "products" ? form.product_ids : [],
      categories: form.scope === "categories" ? form.categories : [],
      starts_at: localToIso(form.starts_at),
      expires_at: localToIso(form.expires_at),
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      max_uses_per_customer: form.max_uses_per_customer ? parseInt(form.max_uses_per_customer) : null,
      min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : null,
      free_shipping: !!form.free_shipping,
      enabled: !!form.enabled
    };

    setSaving(true);
    var req = editing
      ? axios.put(API + "/coupons/" + editing.id, payload)
      : axios.post(API + "/coupons", payload);
    req.then(function () {
      toast.success(editing ? "Coupon updated" : "Coupon created");
      setShowModal(false);
      refresh();
    })
      .catch(function (err) {
        var msg = err.response?.data?.detail || "Failed to save";
        toast.error(msg);
      })
      .finally(function () { setSaving(false); });
  }

  function toggleEnabled(coupon) {
    axios.put(API + "/coupons/" + coupon.id, { enabled: !coupon.enabled })
      .then(refresh)
      .catch(function () { toast.error("Failed to toggle"); });
  }

  function deleteCoupon(coupon) {
    if (!window.confirm("Delete coupon " + coupon.code + "? This cannot be undone.")) return;
    axios.delete(API + "/coupons/" + coupon.id)
      .then(function () { toast.success("Deleted"); refresh(); })
      .catch(function () { toast.error("Failed to delete"); });
  }

  function copyCode(code) {
    navigator.clipboard.writeText(code).then(function () { toast.success("Copied " + code); });
  }

  function formatDiscount(c) {
    return c.discount_type === "percentage" ? c.discount_value + "% OFF" : "$" + parseFloat(c.discount_value).toFixed(2) + " OFF";
  }

  function formatScope(c) {
    if (c.scope === "products") return (c.product_ids || []).length + " product(s)";
    if (c.scope === "categories") return (c.categories || []).length + " category(ies)";
    return "Entire cart";
  }

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="coupons-tab">
      {/* Header */}
      <div className="bg-[#121212] border border-white/5 rounded-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-heading font-bold text-white text-lg uppercase">Coupons</h3>
            <p className="text-[#71717a] text-sm mt-1">Create discount codes customers can enter at checkout.</p>
          </div>
          <Button className="btn-primary" onClick={openCreate} data-testid="new-coupon-btn">
            <Plus className="w-4 h-4 mr-2" /> New Coupon
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#121212] border border-white/5 rounded-sm overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-left">
              <th className="p-3 font-mono text-xs text-[#A1A1AA] uppercase">Code</th>
              <th className="p-3 font-mono text-xs text-[#A1A1AA] uppercase">Discount</th>
              <th className="p-3 font-mono text-xs text-[#A1A1AA] uppercase">Scope</th>
              <th className="p-3 font-mono text-xs text-[#A1A1AA] uppercase">Expires</th>
              <th className="p-3 font-mono text-xs text-[#A1A1AA] uppercase">Uses</th>
              <th className="p-3 font-mono text-xs text-[#A1A1AA] uppercase">Status</th>
              <th className="p-3 font-mono text-xs text-[#A1A1AA] uppercase text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-[#71717a]">No coupons yet. Create your first one above.</td></tr>
            )}
            {coupons.map(function (c) {
              return (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5" data-testid={"coupon-row-" + c.id}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[#C0A062] font-bold">{c.code}</span>
                      <button onClick={function () { copyCode(c.code); }} className="text-[#71717a] hover:text-white" data-testid={"copy-coupon-" + c.id}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {c.description && <p className="text-[#71717a] text-xs mt-0.5">{c.description}</p>}
                  </td>
                  <td className="p-3 text-white font-mono text-sm">
                    {formatDiscount(c)}
                    {c.free_shipping && <span className="ml-1 text-[10px] text-[#8CB369] font-mono uppercase block">+ Free Ship</span>}
                  </td>
                  <td className="p-3 text-[#A1A1AA] text-sm">{formatScope(c)}</td>
                  <td className="p-3 text-[#A1A1AA] text-xs font-mono">{c.expires_at ? formatDate(c.expires_at) : "—"}</td>
                  <td className="p-3 text-[#A1A1AA] text-xs font-mono">
                    {c.total_uses || 0}{c.max_uses ? " / " + c.max_uses : ""}
                  </td>
                  <td className="p-3">
                    <button onClick={function () { toggleEnabled(c); }} data-testid={"toggle-coupon-" + c.id}>
                      {c.enabled
                        ? <span className="inline-flex items-center gap-1 text-[#8CB369] text-xs font-mono uppercase"><ToggleRight className="w-5 h-5" /> Active</span>
                        : <span className="inline-flex items-center gap-1 text-[#71717a] text-xs font-mono uppercase"><ToggleLeft className="w-5 h-5" /> Off</span>}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={function () { openEdit(c); }} className="p-1.5 text-[#A1A1AA] hover:text-[#C0A062]" data-testid={"edit-coupon-" + c.id}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={function () { deleteCoupon(c); }} className="p-1.5 text-[#A1A1AA] hover:text-red-400" data-testid={"delete-coupon-" + c.id}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="coupon-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-sm max-w-2xl w-full p-6 my-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-bold text-white text-lg uppercase flex items-center gap-2">
                <Tag className="w-5 h-5 text-[#C0A062]" />
                {editing ? "Edit Coupon" : "New Coupon"}
              </h3>
              <button onClick={function () { setShowModal(false); }} className="text-[#71717a] hover:text-white" data-testid="close-coupon-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5">
              {/* Code + description */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Code *</Label>
                  <Input
                    value={form.code}
                    onChange={function (e) { setField("code", e.target.value.toUpperCase()); }}
                    placeholder="SAVE10"
                    className="input-tactical font-mono"
                    data-testid="coupon-code-input"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Enabled</Label>
                    <div className="bg-[#121212] border border-white/5 rounded-sm p-2 flex items-center justify-between">
                      <span className="text-white text-sm">{form.enabled ? "Active" : "Disabled"}</span>
                      <Switch checked={form.enabled} onCheckedChange={function (v) { setField("enabled", v); }} data-testid="coupon-enabled-toggle" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Description (internal note)</Label>
                <Textarea
                  value={form.description}
                  onChange={function (e) { setField("description", e.target.value); }}
                  rows={2}
                  className="input-tactical resize-none"
                  placeholder="e.g. Memorial Day 10% off"
                  data-testid="coupon-description-input"
                />
              </div>

              {/* Discount type + value */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Discount Type</Label>
                  <select
                    value={form.discount_type}
                    onChange={function (e) { setField("discount_type", e.target.value); }}
                    className="input-tactical w-full"
                    data-testid="coupon-type-select"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="dollar">Dollar amount ($)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">
                    Value {form.discount_type === "percentage" ? "(%)" : "($)"}
                  </Label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.discount_value}
                    onChange={function (e) { setField("discount_value", e.target.value); }}
                    className="input-tactical font-mono"
                    data-testid="coupon-value-input"
                  />
                </div>
              </div>

              {/* Scope */}
              <div>
                <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Applies To</Label>
                <div className="flex gap-2 mb-2">
                  {["cart", "products", "categories"].map(function (s) {
                    var active = form.scope === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={function () { setField("scope", s); }}
                        className={"px-3 py-1.5 text-xs font-mono uppercase rounded border " + (active ? "bg-[#C0A062] text-black border-[#C0A062]" : "bg-black/40 text-[#A1A1AA] border-white/10")}
                        data-testid={"coupon-scope-" + s}
                      >
                        {s === "cart" ? "Entire Cart" : s === "products" ? "Specific Products" : "Specific Categories"}
                      </button>
                    );
                  })}
                </div>

                {form.scope === "products" && (
                  <div className="bg-[#121212] border border-white/5 rounded-sm p-3 max-h-56 overflow-y-auto" data-testid="coupon-product-list">
                    {products.length === 0 && <p className="text-[#71717a] text-xs">No products available.</p>}
                    {products.map(function (p) {
                      var checked = form.product_ids.indexOf(p.id) >= 0;
                      return (
                        <label key={p.id} className="flex items-center gap-2 py-1 text-sm text-[#D4D4D8] hover:text-white cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={function () { toggleArrayValue("product_ids", p.id); }}
                            data-testid={"coupon-product-toggle-" + p.id}
                          />
                          <span className="truncate">{p.name}</span>
                          <span className="text-[#71717a] text-xs ml-auto">${(p.sale_price || p.price || 0).toFixed(2)}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {form.scope === "categories" && (
                  <div className="bg-[#121212] border border-white/5 rounded-sm p-3 flex flex-wrap gap-2" data-testid="coupon-category-list">
                    {categories.length === 0 && <p className="text-[#71717a] text-xs">No categories.</p>}
                    {categories.map(function (cat) {
                      var checked = form.categories.indexOf(cat) >= 0;
                      return (
                        <button
                          type="button"
                          key={cat}
                          onClick={function () { toggleArrayValue("categories", cat); }}
                          className={"px-3 py-1 text-xs font-mono uppercase rounded border " + (checked ? "bg-[#C0A062] text-black border-[#C0A062]" : "bg-black/40 text-[#A1A1AA] border-white/10")}
                          data-testid={"coupon-category-toggle-" + cat}
                        >
                          {cat}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Starts (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={function (e) { setField("starts_at", e.target.value); }}
                    className="input-tactical"
                    data-testid="coupon-starts-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Expires (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={function (e) { setField("expires_at", e.target.value); }}
                    className="input-tactical"
                    data-testid="coupon-expires-input"
                  />
                </div>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Max Total Uses</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.max_uses}
                    onChange={function (e) { setField("max_uses", e.target.value); }}
                    placeholder="Unlimited"
                    className="input-tactical font-mono"
                    data-testid="coupon-max-uses-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Max Per Customer</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.max_uses_per_customer}
                    onChange={function (e) { setField("max_uses_per_customer", e.target.value); }}
                    placeholder="Unlimited"
                    className="input-tactical font-mono"
                    data-testid="coupon-max-per-customer-input"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Min Order ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.min_order_amount}
                    onChange={function (e) { setField("min_order_amount", e.target.value); }}
                    placeholder="None"
                    className="input-tactical font-mono"
                    data-testid="coupon-min-order-input"
                  />
                </div>
              </div>

              {/* Free shipping */}
              <div className="flex items-center justify-between bg-[#121212] border border-white/5 rounded-sm p-3">
                <div>
                  <Label className="text-white">Free shipping</Label>
                  <p className="text-[10px] text-[#71717a]">Override calculated shipping cost to $0 for orders using this coupon.</p>
                </div>
                <Switch
                  checked={form.free_shipping}
                  onCheckedChange={function (v) { setField("free_shipping", v); }}
                  data-testid="coupon-free-shipping-toggle"
                />
              </div>

              {editing && editing.total_uses > 0 && (
                <div className="flex items-start gap-2 text-xs text-[#A1A1AA] bg-yellow-500/5 border border-yellow-500/20 rounded-sm p-3">
                  <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span>This coupon has been used {editing.total_uses} time{editing.total_uses === 1 ? "" : "s"}. Editing it won't affect past orders.</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-white/5">
              <Button variant="outline" className="border-white/10 text-[#A1A1AA]" onClick={function () { setShowModal(false); }}>
                Cancel
              </Button>
              <Button className="btn-primary" onClick={save} disabled={saving} data-testid="save-coupon-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editing ? "Save Changes" : "Create Coupon"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
